import React from 'react';

import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';

import fonts from 'theme/fonts';
import promoColors from 'theme/promoColors';
import promoMedia from 'theme/promoMedia';

const getGradientColor = (gradientColor) => {
    switch (gradientColor) {
        case 'aubergine01':
            return promoColors.aubergine01;
        case 'aubergine02':
            return promoColors.aubergine02;
        case 'aubergine03':
            return promoColors.aubergine03;
        case 'passionFruit01':
            return promoColors.passionFruit01;
        case 'passionFruit02':
            return promoColors.passionFruit02;
        case 'passionFruit03':
            return promoColors.passionFruit03;
        case 'oceanBlue01':
            return promoColors.oceanBlue01;
        case 'oceanBlue02':
            return promoColors.oceanBlue02;
        case 'oceanBlue03':
            return promoColors.oceanBlue03;
        case 'blueberryBlue01':
            return promoColors.blueberryBlue01;
        case 'blueberryBlue02':
            return promoColors.blueberryBlue02;
        case 'blueberryBlue03':
            return promoColors.blueberryBlue03;
        case 'jade01':
            return promoColors.jade01;
        case 'jade02':
            return promoColors.jade02;
        case 'jade03':
            return promoColors.jade03;
        default:
            return promoColors.passionFruit01;
    }
};
const getMobileSize = (props) => {
    switch (props.variant) {
        case 'h1':
            return '40px';
        case 'h2':
            return '32px';
        case 'h3':
            return '24px';
        case 'h4':
            return '20px';
        case 'subheader':
            return '18px';
        case 'quote':
            return '16px';
        case 'label':
        case 'note':
            return '12px';
        case 'link':
        case 'div':
        case 'span':
            return '14px';
        default:
            return '14px';
    }
};
const getMobileLineHeight = (props) => {
    switch (props.variant) {
        case 'h1':
            return '46px';
        case 'h2':
            return '38px';
        case 'h3':
        case 'h4':
        case 'link':
            return 'normal';
        case 'subheader':
            return '24px';
        case 'quote':
            return '24px';
        case 'label':
        case 'note':
            return '16px';
        case 'div':
        case 'span':
            return '20px';
        default:
            return '20px';
    }
};
const getDesktopSize = (props) => {
    switch (props.variant) {
        case 'h1':
            return '56px';
        case 'h2':
            return '44px';
        case 'h3':
            return '36px';
        case 'h4':
            return '32px';
        case 'subheader':
            return '21px';
        case 'quote':
            return '22px';
        case 'label':
        case 'note':
            return '12px';
        case 'link':
        case 'div':
        case 'span':
            return '16px';
        default:
            return '16px';
    }
};
const getDesktopLineHeight = (props) => {
    switch (props.variant) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
            return '125%';
        case 'subheader':
            return '28px';
        case 'quote':
            return '32px';
        case 'link':
            return 'normal';
        case 'label':
        case 'note':
            return '16px';
        case 'div':
        case 'span':
            return '24px';
        default:
            return '24px';
    }
};

const getFontWeight = (props) => {
    if (props.isBold) {
        return fonts.bold;
    } else if (props.variant === 'h1' || props.variant === 'h2' || props.variant === 'h3' || props.variant === 'h4' || props.variant === 'label') {
        return fonts.medium;
    } else {
        return fonts.regular;
    }
};

const getLetterSpacing = (props) => {
    if (props.variant === 'h1' || props.variant === 'h2' || props.variant === 'h3' || props.variant === 'h4') {
        return '0.01em';
    } else if (props.variant === 'label') {
        return '0.05em';
    } else if (props.variant === 'quote') {
        return '0.02em';
    } else {
        return null;
    }
};

const getComponent = (tag) => {
    switch (tag) {
        case 'h1':
            return TextHeader1;
        case 'h2':
            return TextHeader2;
        case 'h3':
            return TextHeader3;
        case 'h4':
            return TextHeader4;
        case 'div':
            return TextDiv;
        case 'link':
            return TextLink;
        case 'label':
            return TextLabel;
        case 'listItem':
            return TextListItem;
        case 'p':
            return TextParagraph;
        default:
            return TextSpan;
    }
};

const CommonCss = css`
    font-size: ${(props) => getMobileSize(props)};
    line-height: ${(props) => getMobileLineHeight(props)};
    color: ${(props) => (props.tag === 'link' ? promoColors.primary : props.gradientColor ? null : promoColors[props.color])};
    background: ${(props) => (props.gradientColor ? getGradientColor(props.gradientColor) : null)};
    -webkit-background-clip: ${(props) => (props.gradientColor ? 'text' : null)};
    -webkit-text-fill-color: ${(props) => (props.gradientColor ? 'transparent' : null)};
    font-family: ${(props) => getFontWeight(props)};
    font-weight: ${(props) => (props.isBold ? '600' : '400')};
    white-space: ${(props) => (props.nowrap || props.ellipsis ? 'nowrap' : null)};
    overflow: ${(props) => (props.ellipsis ? 'hidden' : null)};
    text-overflow: ${(props) => (props.ellipsis ? 'ellipsis' : null)};
    text-align: ${(props) => (props.textAlign ? props.textAlign : null)};
    text-transform: ${(props) => (props.variant === 'label' ? 'uppercase' : null)};
    letter-spacing: ${(props) => getLetterSpacing(props)};
    text-decoration: ${(props) => (props.tag === 'link' ? 'none' : null)};
    cursor: ${(props) => (props.cursorPointer ? 'pointer' : null)};
    font-variant: ${(props) => (props.smallCaps ? 'small-caps' : null)};

    &:link,
    &:visited {
        color: ${promoColors.primary};
    }

    &:hover {
        color: ${(props) => (props.tag === 'link' ? promoColors.purple40 : null)};
    }

    ${promoMedia.desktop1440} {
        font-size: ${(props) => getDesktopSize(props)};
        line-height: ${(props) => getDesktopLineHeight(props)};
    }
`;

const TextHeader1 = styled.h1`
    ${CommonCss};
`;
const TextHeader2 = styled.h2`
    ${CommonCss};
`;
const TextHeader3 = styled.h3`
    ${CommonCss};
`;
const TextHeader4 = styled.h4`
    ${CommonCss};
`;
const TextDiv = styled.div`
    ${CommonCss};
`;
const TextSpan = styled.span`
    ${CommonCss};
`;
const TextParagraph = styled.p`
    ${CommonCss};
`;
const TextLink = styled.a`
    ${CommonCss};
`;
const TextLabel = styled.label`
    ${CommonCss};
`;
const TextListItem = styled.li`
    ${CommonCss};
`;

export const PromoText = ({ color = 'baseColor', ...props }) => {
    const Wrapper = getComponent(props.tag);
    return (
        <Wrapper color={color} {...props}>
            {props.children}
        </Wrapper>
    );
};

PromoText.propTypes = {
    variant: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'subheader', 'quote', 'div', 'span', 'link', 'label', 'note']),
    tag: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'div', 'span', 'link', 'label', 'p', 'listItem']),
    color: PropTypes.oneOf([
        'baseColor',
        'primary',
        'primaryB2B',
        'purple80',
        'purple40',
        'purple20',
        'gray80',
        'gray40',
        'gray20',
        'white',
        'whiteRgba48',
        'error',
        'success',
    ]),
    gradientColor: PropTypes.oneOf([
        'aubergine01',
        'aubergine02',
        'aubergine03',
        'passionFruit01',
        'passionFruit02',
        'passionFruit03',
        'oceanBlue01',
        'oceanBlue02',
        'oceanBlue03',
        'blueberryBlue01',
        'blueberryBlue02',
        'blueberryBlue03',
        'jade01',
        'jade02',
        'jade03',
    ]),
    nowrap: PropTypes.bool,
    ellipsis: PropTypes.bool,
    textAlign: PropTypes.oneOf(['left', 'center', 'right']),
    isBold: PropTypes.bool,
    href: PropTypes.string,
    cursorPointer: PropTypes.bool,
    smallCaps: PropTypes.bool,
};
